import React from 'react';

interface UseInfiniteIntersectionObserverParams {
  onIntersectSync: () => void;
  options?: IntersectionObserverInit;
}

type UseIntersectionObserverPayload<
  IntersectRefElementType extends HTMLElement,
  RootRefElementType extends HTMLElement,
> = [
  ref: (node: IntersectRefElementType) => void,
  payload: {
    isIntersecting: boolean;
    rootRef?: React.RefObject<RootRefElementType>;
  },
];

export const useIntersectionObserver = <
  IntersectRefElementType extends HTMLElement,
  RootRefElementType extends HTMLElement,
>({
  onIntersectSync,
  options = {},
}: UseInfiniteIntersectionObserverParams): UseIntersectionObserverPayload<
  IntersectRefElementType,
  RootRefElementType
> => {
  const rootRef = React.useRef<RootRefElementType | null>(null);

  const { threshold = 1, root = rootRef.current, rootMargin = '0px' } = options;

  const [entry, setEntry] = React.useState<IntersectionObserverEntry | null>(
    null,
  );

  const isHandling = React.useRef<boolean>(false);
  const previousObserver = React.useRef<IntersectionObserver | null>(null);

  const intersectionRef = React.useCallback(
    (node: HTMLElement) => {
      if (previousObserver.current) {
        previousObserver.current.disconnect();
        previousObserver.current = null;
      }

      if (node?.nodeType === Node.ELEMENT_NODE) {
        const observer = new IntersectionObserver(
          ([entry]) => {
            setEntry(entry);
          },
          { threshold, root, rootMargin },
        );

        observer.observe(node);
        previousObserver.current = observer;
      }
    },
    [root, rootMargin, threshold],
  );

  // If the element is no longer intersecting, reset the flag so the next time it intersects, we can handle it again
  if (!entry?.isIntersecting) {
    isHandling.current = false;
  }

  // If the element is intersecting and we haven't handled it yet, handle it
  if (entry?.isIntersecting && !isHandling.current) {
    isHandling.current = true;
    onIntersectSync();
  }

  return [
    intersectionRef,
    { isIntersecting: entry?.isIntersecting ?? false, rootRef },
  ];
};
